import { useRef, useState } from 'react';
import {
  MdCode,
  MdFormatBold,
  MdFormatItalic,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatQuote,
  MdHorizontalRule,
  MdImage,
  MdLink,
  MdRedo,
  MdStrikethroughS,
  MdUndo,
} from 'react-icons/md';
import { FiTerminal } from 'react-icons/fi';
import { useEditorState } from '@tiptap/react';
import { ACCEPTED_IMAGE_TYPES } from './imageUpload';
import styles from './NewPost.module.css';

const normalizeUrl = (value) => {
  const url = value.trim();
  if (!url || url.startsWith('#') || url.startsWith('/') || /^\w+:/.test(url)) return url;
  return `https://${url}`;
};

const ToolbarButton = ({ label, active = false, disabled = false, onClick, children }) => (
  <button
    type="button"
    className={`${styles.toolbarButton} ${active ? styles.activeButton : ''}`}
    aria-label={label}
    aria-pressed={active}
    title={label}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </button>
);

const EditorToolbar = ({ editor, previewMode, uploadImage }) => {
  const [panel, setPanel] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [image, setImage] = useState({ src: '', alt: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInput = useRef(null);
  const editorState = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      paragraph: current?.isActive('paragraph'),
      heading1: current?.isActive('heading', { level: 1 }),
      heading2: current?.isActive('heading', { level: 2 }),
      heading3: current?.isActive('heading', { level: 3 }),
      bold: current?.isActive('bold'),
      italic: current?.isActive('italic'),
      strike: current?.isActive('strike'),
      code: current?.isActive('code'),
      bulletList: current?.isActive('bulletList'),
      orderedList: current?.isActive('orderedList'),
      blockquote: current?.isActive('blockquote'),
      codeBlock: current?.isActive('codeBlock'),
      link: current?.isActive('link'),
      canUndo: current?.can().chain().focus().undo().run(),
      canRedo: current?.can().chain().focus().redo().run(),
    }),
  }) || {};

  if (!editor || previewMode) return null;

  const openLink = () => {
    setLinkUrl(editor.getAttributes('link').href || '');
    setPanel(panel === 'link' ? null : 'link');
  };

  const applyLink = () => {
    const url = normalizeUrl(linkUrl);
    if (!url) editor.chain().focus().extendMarkRange('link').unsetLink().run();
    else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    setPanel(null);
  };

  const addImage = () => {
    const src = normalizeUrl(image.src);
    if (!src) return;
    editor.chain().focus().setImage({ src, alt: image.alt.trim() }).run();
    setImage({ src: '', alt: '' });
    setPanel(null);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const src = await uploadImage(file);
      editor.chain().focus().setImage({ src, alt: image.alt.trim() }).run();
      setImage({ src: '', alt: '' });
      setPanel(null);
    } catch (error) {
      setUploadError(error.message || 'Image upload failed.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const headingValue = editorState.heading1 ? '1' : editorState.heading2 ? '2' : editorState.heading3 ? '3' : '0';

  return (
    <div className={styles.toolbarWrap}>
      <div className={styles.toolbar}>
        <select
          className={styles.headingSelect}
          value={headingValue}
          aria-label="Text style"
          onChange={(event) => {
            const level = Number(event.target.value);
            if (level === 0) editor.chain().focus().setParagraph().run();
            else editor.chain().focus().setHeading({ level }).run();
          }}
        >
          <option value="0">Paragraph</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>

        <span className={styles.divider} />
        <ToolbarButton label="Bold" active={editorState.bold} onClick={() => editor.chain().focus().toggleBold().run()}><MdFormatBold /></ToolbarButton>
        <ToolbarButton label="Italic" active={editorState.italic} onClick={() => editor.chain().focus().toggleItalic().run()}><MdFormatItalic /></ToolbarButton>
        <ToolbarButton label="Strikethrough" active={editorState.strike} onClick={() => editor.chain().focus().toggleStrike().run()}><MdStrikethroughS /></ToolbarButton>
        <ToolbarButton label="Inline code" active={editorState.code} onClick={() => editor.chain().focus().toggleCode().run()}><MdCode /></ToolbarButton>

        <span className={styles.divider} />
        <ToolbarButton label="Bullet list" active={editorState.bulletList} onClick={() => editor.chain().focus().toggleBulletList().run()}><MdFormatListBulleted /></ToolbarButton>
        <ToolbarButton label="Ordered list" active={editorState.orderedList} onClick={() => editor.chain().focus().toggleOrderedList().run()}><MdFormatListNumbered /></ToolbarButton>
        <ToolbarButton label="Blockquote" active={editorState.blockquote} onClick={() => editor.chain().focus().toggleBlockquote().run()}><MdFormatQuote /></ToolbarButton>
        <ToolbarButton label="Code block" active={editorState.codeBlock} onClick={() => editor.chain().focus().toggleCodeBlock().run()}><FiTerminal /></ToolbarButton>
        <ToolbarButton label="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}><MdHorizontalRule /></ToolbarButton>

        <span className={styles.divider} />
        <ToolbarButton label="Add or edit link" active={editorState.link} onClick={openLink}><MdLink /></ToolbarButton>
        <ToolbarButton label="Insert image from URL" onClick={() => setPanel(panel === 'image' ? null : 'image')}><MdImage /></ToolbarButton>

        <span className={styles.toolbarSpacer} />
        <ToolbarButton label="Undo" disabled={!editorState.canUndo} onClick={() => editor.chain().focus().undo().run()}><MdUndo /></ToolbarButton>
        <ToolbarButton label="Redo" disabled={!editorState.canRedo} onClick={() => editor.chain().focus().redo().run()}><MdRedo /></ToolbarButton>
      </div>

      {panel === 'link' && (
        <div className={styles.inlinePanel}>
          <div><strong>Link</strong><span>Paste a URL or clear it to remove the link.</span></div>
          <input value={linkUrl} onChange={(event) => setLinkUrl(event.target.value)} placeholder="https://example.com" autoFocus />
          <button type="button" onClick={applyLink}>Apply</button>
          <button type="button" className={styles.textButton} onClick={() => setPanel(null)}>Cancel</button>
        </div>
      )}

      {panel === 'image' && (
        <div className={`${styles.inlinePanel} ${styles.imagePanel}`}>
          <div><strong>Image</strong><span>{uploadError || 'Upload a file or paste a public URL.'}</span></div>
          <button type="button" disabled={uploading} onClick={() => fileInput.current?.click()}>{uploading ? 'Uploading…' : 'Upload'}</button>
          <input ref={fileInput} className={styles.hiddenFileInput} type="file" accept={ACCEPTED_IMAGE_TYPES.join(',')} onChange={(event) => handleFileUpload(event.target.files?.[0])} />
          <input value={image.src} onChange={(event) => setImage((current) => ({ ...current, src: event.target.value }))} placeholder="https://.../image.webp" autoFocus />
          <input value={image.alt} onChange={(event) => setImage((current) => ({ ...current, alt: event.target.value }))} placeholder="Alt text" />
          <button type="button" onClick={addImage}>Insert</button>
          <button type="button" className={styles.textButton} onClick={() => setPanel(null)}>Cancel</button>
        </div>
      )}
    </div>
  );
};

export default EditorToolbar;
